<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import GbButton from '@/components/GbButton.vue'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/tooltip'
import { useCopyText } from '@/util/use_copy_text'
import { announce } from '@/util/live_announcer'

const props = defineProps({
  stringToCopy: {
    type: String,
    required: true
  },
  tooltipText: {
    type: String,
    required: false,
  },
  buttonText: {
    type: String,
    required: true,
    default: 'Copy'
  },
  buttonTheme: {
    type: String,
    required: false,
    default: 'bluePrimary'
  },
  customClass: {
    type: String,
    required: false
  },
  autoHideTooltip: {
    type: Boolean,
    default: false
  },
  autoHideDelay: {
    type: Number,
    default: 2000 // 2 seconds
  },
  size: {
    type: String,
    required: false,
    default: 'md'
  },
  iconClass: {
    type: String,
    required: false,
    default: 'material-icons-round'
  },
  ariaLabel: {
    type: String,
    required: false
  },
  errorText: {
    type: String,
    default: 'Error copying text. Use Ctrl+C instead.'
  },
  // WCAG 2.2.1: once an auto-hidden confirmation is gone, the check keeps saying the text was copied.
  showCopiedCheck: {
    type: Boolean,
    default: false
  },
  isIconShown: {
    type: Boolean,
    default: true
  }
})
const emit = defineEmits(['copied'])

const { isTooltipOpen, copyText, showTooltipUntilDismissed, hideTooltip } = useCopyText()
const copyFailed = ref(false)
const hasCopiedCurrentText = ref(false)
const isCopiedCheckShown = computed(() => props.showCopiedCheck && hasCopiedCurrentText.value)
const accessibleLabel = computed(() => props.ariaLabel && isCopiedCheckShown.value ? `${props.ariaLabel}, copied` : props.ariaLabel)

watch(() => props.stringToCopy, () => {
  hasCopiedCurrentText.value = false
})

const clickAction = async (event: MouseEvent) => {
  const buttonElement = event.currentTarget as HTMLElement | null
  const copiedText = props.stringToCopy
  copyFailed.value = false
  try {
    await copyText(copiedText, event, {
      autoHideTooltip: props.autoHideTooltip,
      autoHideDelay: props.autoHideDelay,
      onCopied: () => {
        hasCopiedCurrentText.value = copiedText === props.stringToCopy
        announce(props.tooltipText || 'Copied')
        emit('copied')
      }
    })
  } catch {
    copyFailed.value = true
    hasCopiedCurrentText.value = false
    announce(props.errorText)
    showTooltipUntilDismissed(buttonElement, props.autoHideDelay)
  }
}
</script>
<template>
  <TooltipProvider :disabled="!isTooltipOpen">
    <Tooltip :open="isTooltipOpen" @update:open="(val) => !isTooltipOpen && (isTooltipOpen = val)">
      <TooltipTrigger as-child>
        <GbButton
          type="button"
          :gbTheme="props.buttonTheme"
          :size="props.size"
          :class="props.customClass"
          :aria-label="accessibleLabel"
          @click="clickAction"
        >
          <slot v-if="props.isIconShown" name="icon">
            <span :class="props.iconClass" class="mr-1 text-sm" aria-hidden="true">file_copy</span>
          </slot> {{ props.buttonText }}
          <template v-if="isCopiedCheckShown">
            <span class="material-icons-round ml-1 text-sm" aria-hidden="true" data-testid="copied-check">check</span>
            <span class="sr-only">, copied</span>
          </template>
        </GbButton>
      </TooltipTrigger>
      <TooltipContent v-if="copyFailed || props.tooltipText" @escape-key-down="hideTooltip">
        <p>{{ copyFailed ? props.errorText : props.tooltipText }}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
